import { Header, SortingState } from '@tanstack/react-table'
import { useCallback } from 'react'
import { SortableHeaderButton } from './SortableHeaderButton'

type SortableHeaderCellProps<TData> = {
  header: Header<TData, unknown>
  onSortingChange: (sorting: SortingState) => void
}

/**
 * Sortable Header Cell
 *
 * Renders a table header cell (`<th>`) with sorting functionality. Generic over the table's row
 * type because the body only reads `header.column`, so any TanStack table can use it.
 */
export function SortableHeaderCell<TData>({ header, onSortingChange }: SortableHeaderCellProps<TData>) {
  const isSorted = header.column.getIsSorted()
  const columnId = header.column.id

  const columnMeta = header.column.columnDef.meta
  const defaultSortDesc = columnMeta?.defaultSortDesc ?? false
  const sortLabels = columnMeta?.sortLabels

  const handleSort = useCallback(() => {
    if (isSorted) {
      // If already sorted, toggle direction
      onSortingChange([{ id: columnId, desc: isSorted === 'asc' }])
    } else {
      // Use the default sort direction from column meta
      onSortingChange([{ id: columnId, desc: defaultSortDesc }])
    }
  }, [isSorted, columnId, defaultSortDesc, onSortingChange])

  // Determine aria-sort value for accessibility
  const ariaSortValue = !isSorted ? 'none' : isSorted === 'asc' ? 'ascending' : 'descending'

  // All columns use string headers
  const label = header.column.columnDef.header as string

  return (
    <th className={columnMeta?.headerClassName} aria-sort={ariaSortValue}>
      <SortableHeaderButton
        label={label}
        isSorted={isSorted}
        onSort={handleSort}
        ascendingLabel={sortLabels?.ascendingLabel}
        descendingLabel={sortLabels?.descendingLabel}
        defaultSortDesc={defaultSortDesc}
      />
    </th>
  )
}
